import { useMutation } from 'react-query';
import { ADD_UPI_ID, SUBMIT_BANK_DETAILS } from '../appConstants/APIEndpoint';
import { requestApiPost } from './index';
import type {
  IAddBankRequest,
  IAddModeResponse,
  IAddUpiRequest,
  IPayoutDetails,
} from '../interfaces/Withdrawal';

/**
 * Adding a withdrawal mode.
 * Mirrors `UpiBottomSheet.addUPICall` and `AddBankAccountFragment.callAddBankApi`.
 *
 * Both endpoints return the updated `payoutDetails`, which the caller writes
 * back into the withdrawal store so the new mode is selected on return.
 */

export const addUpiId = async (
  body: IAddUpiRequest,
): Promise<IPayoutDetails | undefined> => {
  const res = await requestApiPost<IAddModeResponse>(ADD_UPI_ID, body);
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || 'This UPI ID could not be linked');
  }
  return response?.data?.payoutDetails;
};

export const submitBankDetails = async (
  body: IAddBankRequest,
): Promise<IPayoutDetails | undefined> => {
  const res = await requestApiPost<IAddModeResponse>(SUBMIT_BANK_DETAILS, body);
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || 'This bank account could not be linked');
  }
  return response?.data?.payoutDetails;
};

export const useAddUpi = () => {
  const { mutate, mutateAsync, isLoading, error, reset } = useMutation<
    IPayoutDetails | undefined,
    Error,
    IAddUpiRequest
  >((body) => addUpiId(body), { retry: false });

  return { addUpi: mutate, addUpiAsync: mutateAsync, isLoading, error, reset };
};

export const useAddBankAccount = () => {
  const { mutate, mutateAsync, isLoading, error, reset } = useMutation<
    IPayoutDetails | undefined,
    Error,
    IAddBankRequest
  >((body) => submitBankDetails(body), { retry: false });

  return {
    addBankAccount: mutate,
    addBankAccountAsync: mutateAsync,
    isLoading,
    error,
    reset,
  };
};
